import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import type { AxiosError, AxiosResponse } from "axios";
import {
  type AdminPlatformResponse,
  AdminPlatformsApi,
  type AdminRedirectResponse,
  AdminRedirectsApi,
  type CreatePlatformRequest,
  type CreateRedirectRequest,
  type PaginatedListOfAdminListPlatformResponse,
  type UpdatePlatformRequest,
  type UpdateRedirectRequest,
} from "mws-platform-ts-sdk";
import { computed, type Ref } from "vue";

import type { UseQueryOptionsOmitQueryKey } from "@/common/types";

const platformApiUrl = import.meta.env.VITE_PLATFORM_API;
const adminPlatformsApi = new AdminPlatformsApi(undefined, platformApiUrl);
const adminRedirectsApi = new AdminRedirectsApi(undefined, platformApiUrl);

export const useGetPlatformQuery = (
  platformId: Ref<string>,
  options?: UseQueryOptionsOmitQueryKey<AdminPlatformResponse>,
) => {
  const queryKey = ["platforms", platformId];

  const queryFn = async () => {
    const response = await adminPlatformsApi.adminPlatformsPlatformIdGet({
      platformId: platformId.value,
    });

    return response.data;
  };

  return useQuery({ queryKey, queryFn, ...options });
};

export const useGetPlatformsQuery = (
  searchTerm?: Ref<string>,
  pageSize?: Ref<number>,
  offset?: Ref<number>,
  filters?: Ref<{
    platformIds?: string[];
    isArchived?: boolean;
  }>,
  options?: UseQueryOptionsOmitQueryKey<PaginatedListOfAdminListPlatformResponse>,
) => {
  const queryParams = computed(() => ({
    platformIds: filters?.value?.platformIds?.join(","),
    isArchived: filters?.value.isArchived,
    searchTerm: searchTerm?.value ?? "",
    offset: offset?.value ?? 0,
    size: pageSize?.value ?? 100,
  }));

  const queryKey = ["platforms", queryParams];

  const queryFn = async () => {
    const response = await adminPlatformsApi.adminPlatformsGet(
      queryParams.value,
    );

    return response.data;
  };

  const opts = { staleTime: Infinity, ...options };

  return useQuery({ queryKey, queryFn, ...opts });
};

export const useTogglePlatformArchiveMutation = () => {
  const mutationFn = async ({
    platformId,
    isArchived,
  }: {
    platformId: string;
    isArchived: boolean;
  }) => {
    const response =
      await adminPlatformsApi.adminPlatformsPlatformIdArchivePatch({
        platformId,
        platformArchiveRequest: { isArchived },
      });

    return response.data;
  };

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["platforms"] });
    },
  });
};

export const useCreatePlatformMutation = () => {
  return useMutation({
    mutationFn: async (request: CreatePlatformRequest) => {
      const response = await adminPlatformsApi.adminPlatformsPost({
        createPlatformRequest: request,
      });

      return response.data;
    },
  });
};

export const useUpdatePlatformMutation = (
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      platformId: string;
      data: UpdatePlatformRequest;
    }) => {
      const response = await adminPlatformsApi.adminPlatformsPlatformIdPatch({
        platformId: request.platformId,
        updatePlatformRequest: request.data,
      });

      return response.data;
    },
    onSuccess: (_, request) => {
      queryClient.invalidateQueries({
        queryKey: ["platforms", request.platformId],
      });
    },
    onError,
  });
};

export const useCreateRedirectMutation = (
  onSuccess: (response: AdminRedirectResponse) => void,
  onError: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      platformId: string;
      createRequest: CreateRedirectRequest;
    }) => {
      const response =
        await adminRedirectsApi.adminPlatformsPlatformIdRedirectsPost({
          platformId: request.platformId,
          createRedirectRequest: request.createRequest,
        });

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["platforms", data.platformId],
      });
      onSuccess(data);
    },
    onError,
  });
};

export const useUpdateRedirectMutation = (
  onSuccess: (response: AdminRedirectResponse) => void,
  onError: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      platformId: string;
      redirectId: string;
      updateRedirectRequest: UpdateRedirectRequest;
    }) => {
      const response =
        await adminRedirectsApi.adminPlatformsPlatformIdRedirectsRedirectIdPatch(
          {
            platformId: request.platformId,
            redirectId: request.redirectId,
            updateRedirectRequest: request.updateRedirectRequest,
          },
        );

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["platforms", data.platformId],
      });
      onSuccess(data);
    },
    onError,
  });
};

export const useDeleteRedirectMutation = (
  onSuccess: (response: AxiosResponse) => void,
  onError: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { platformId: string; redirectId: string }) => {
      return await adminRedirectsApi.adminPlatformsPlatformIdRedirectsRedirectIdDelete(
        { platformId: request.platformId, redirectId: request.redirectId },
      );
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["platforms"] });
      onSuccess(data);
    },
    onError,
  });
};
